import { createMuiTheme } from '@material-ui/core';

const headingFont = '"Sora", "Plus Jakarta Sans", "Segoe UI", sans-serif';
const bodyFont = '"Plus Jakarta Sans", "Segoe UI", Roboto, Arial, sans-serif';

const baseTypography = {
    useNextVariants: true,
    fontFamily: bodyFont,
    h1: {
        fontFamily: headingFont,
        fontSize: '3.5rem',
        lineHeight: 1,
        fontWeight: 700,
        letterSpacing: '-0.04em',
    },
    h2: {
        fontFamily: headingFont,
        fontSize: '2.7rem',
        lineHeight: 1.05,
        fontWeight: 700,
        letterSpacing: '-0.035em',
    },
    h3: {
        fontFamily: headingFont,
        fontSize: '2.2rem',
        lineHeight: 1.1,
        fontWeight: 700,
        letterSpacing: '-0.03em',
    },
    h4: {
        fontFamily: headingFont,
        fontSize: '1.8rem',
        lineHeight: 1.12,
        fontWeight: 800,
        letterSpacing: '-0.02em',
    },
    h5: {
        fontFamily: headingFont,
        fontSize: '1.3rem',
        lineHeight: 1.2,
        fontWeight: 700,
    },
    h6: {
        fontFamily: headingFont,
        fontSize: '1.08rem',
        lineHeight: 1.25,
        fontWeight: 700,
    },
    subtitle1: {
        fontSize: '1.02rem',
        lineHeight: 1.75,
    },
    body1: {
        fontSize: '0.98rem',
        lineHeight: 1.8,
    },
    button: {
        fontWeight: 700,
        letterSpacing: '0.01em',
    },
};

const brandTheme = (palette) => createMuiTheme({
    palette,
    shape: {
        borderRadius: 12,
    },
    typography: {
        ...baseTypography,
        h4: {
            ...baseTypography.h4,
            color: palette.text.primary,
        },
        h5: {
            ...baseTypography.h5,
            color: palette.text.primary,
        },
        h6: {
            ...baseTypography.h6,
            color: palette.text.primary,
        },
    },
    overrides: {
        MuiCssBaseline: {
            '@global': {
                body: {
                    backgroundColor: palette.background.default,
                    color: palette.text.primary,
                    backgroundImage: palette.type === 'dark'
                        ? 'linear-gradient(180deg, rgba(12, 16, 24, 0.98), rgba(18, 24, 34, 1))'
                        : 'linear-gradient(180deg, rgba(249, 250, 255, 0.98), rgba(239, 242, 248, 1))',
                    backgroundAttachment: 'fixed',
                },
                a: {
                    color: palette.primary.main,
                },
                '::selection': {
                    backgroundColor: palette.secondary.main,
                    color: palette.secondary.contrastText,
                },
            }
        },
        MuiAppBar: {
            colorPrimary: {
                backgroundColor: palette.type === 'dark' ? 'rgba(12, 15, 24, 0.88)' : 'rgba(248, 249, 253, 0.92)',
                backdropFilter: 'blur(14px) saturate(135%)',
                borderBottom: `1px solid ${palette.divider}`,
                boxShadow: 'none',
            }
        },
        MuiButton: {
            root: {
                borderRadius: 999,
                paddingLeft: 18,
                paddingRight: 18,
                textTransform: 'none',
            },
            containedPrimary: {
                backgroundImage: `linear-gradient(135deg, ${palette.secondary.main}, ${palette.primary.main})`,
                boxShadow: 'none',
                '&:hover': {
                    backgroundImage: `linear-gradient(135deg, ${palette.secondary.dark}, ${palette.primary.dark})`,
                    boxShadow: 'none',
                },
            },
            outlinedPrimary: {
                borderColor: palette.divider,
            }
        },
        MuiCard: {
            root: {
                backgroundImage: palette.type === 'dark'
                    ? 'linear-gradient(180deg, rgba(28, 34, 46, 0.76), rgba(20, 24, 32, 0.72))'
                    : 'linear-gradient(180deg, rgba(255, 255, 255, 0.72), rgba(245, 247, 252, 0.68))',
                border: `1px solid ${palette.divider}`,
                backdropFilter: 'blur(14px) saturate(135%)',
                boxShadow: 'none',
            }
        },
        MuiPaper: {
            root: {
                backgroundColor: palette.type === 'dark' ? 'rgba(23, 27, 34, 0.72)' : 'rgba(251, 251, 255, 0.72)',
                backgroundImage: 'none',
                border: `1px solid ${palette.divider}`,
                backdropFilter: 'blur(14px) saturate(135%)',
                boxShadow: 'none',
            }
        },
        MuiMenu: {
            paper: {
                borderRadius: 18,
            }
        },
        MuiDialog: {
            paper: {
                borderRadius: 22,
            }
        },
        MuiDivider: {
            root: {
                backgroundColor: palette.divider,
            }
        },
        MuiOutlinedInput: {
            root: {
                backgroundColor: palette.type === 'dark' ? 'rgba(18, 22, 31, 0.9)' : 'rgba(255, 255, 255, 0.92)',
                position: 'relative',
                borderRadius: 12,
                '& $notchedOutline': {
                    borderColor: palette.divider,
                },
                '&:hover $notchedOutline': {
                    borderColor: palette.primary.main,
                },
                '&$focused $notchedOutline': {
                    borderColor: palette.primary.main,
                    boxShadow: 'none',
                },
            },
            notchedOutline: {},
            focused: {},
        },
        MuiInputLabel: {
            root: {
                color: palette.text.secondary,
            }
        },
        MuiChip: {
            outlined: {
                borderColor: palette.divider,
                backgroundColor: palette.type === 'dark' ? 'rgba(20, 24, 34, 0.72)' : 'rgba(255, 255, 255, 0.7)',
            }
        },
        MuiLink: {
            root: {
                fontWeight: 600,
            }
        }
    }
});

const lightTheme = brandTheme({
    type: 'light',
    primary: {
        main: '#7e67f8',
        light: '#b2a5ff',
        dark: '#5b43d4',
        contrastText: '#ffffff',
    },
    secondary: {
        main: '#c8b1ff',
        light: '#e5d9ff',
        dark: '#9f8cff',
        contrastText: '#22183f',
    },
    info: {
        main: '#a792ff',
        contrastText: '#20173c',
    },
    text: {
        primary: '#151b2a',
        secondary: '#5b6478',
    },
    divider: 'rgba(124, 93, 255, 0.18)',
    background: {
        default: '#eff1f8',
        paper: '#fbfbff',
        elevated: '#f5f0ff',
        subtle: '#eef2ff',
    }
});

const darkTheme = brandTheme({
    type: 'dark',
    primary: {
        main: '#c8b1ff',
        light: '#e3d7ff',
        dark: '#9f84ff',
        contrastText: '#121212',
    },
    secondary: {
        main: '#7c5dff',
        light: '#9f8cff',
        dark: '#6d4df3',
        contrastText: '#f7f8ff',
    },
    info: {
        main: '#9f8cff',
        contrastText: '#121212',
    },
    background: {
        default: '#121212',
        paper: '#171b22',
        elevated: '#1d2330',
        subtle: '#141923',
    },
    text: {
        primary: '#f5f7fb',
        secondary: '#b8c0d4',
    },
    divider: 'rgba(200, 170, 255, 0.16)',
});

export const agentAccentGradients = {
    light: 'linear-gradient(135deg, #1f6f4a, #7c3aed)',
    dark: 'linear-gradient(135deg, #c6ef9a, #d8c7ff)',
};

const themes = {
    light: lightTheme,
    dark: darkTheme
};

export const defaultThemeName = 'dark';

export default themes;
